import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { MappedPackage, usePackages } from "./hooks/UsePackages";
import { GemIconPurple } from "../../../../../../../components/icons/GemIconPurple";
import { GemIconGreen } from "../../../../../../../components/icons/GemIconGreen";
import { GemIconRed } from "../../../../../../../components/icons/GemIconRed";

interface PackageSectionProps {
  defaultPackageName?: string;
  packageType?: string;
  onPackageChange?: (pkg: MappedPackage | null) => void;
}

const iconByIndex = [
  <GemIconPurple key="purple" />,
  <GemIconGreen key="green" />,
  <GemIconRed key="red" />,
];

export default function PackageSection({
  defaultPackageName,
  packageType,
  onPackageChange,
}: PackageSectionProps) {
  const { packages, loading, error } = usePackages(packageType);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (defaultPackageName && packages.length > 0) {
      const match = packages.find((p) => p.name === defaultPackageName);
      if (match) setSelectedId(match.id);
    }
  }, [defaultPackageName, packages]);

  const handleSelect = (pkg: MappedPackage) => {
    setSelectedId(pkg.id);
    onPackageChange?.(pkg);
  };

  if (loading) {
    return (
      <Paper
        sx={{
          borderRadius: "14px",
          p: 2.5,
          boxShadow: 0,
          mb: 2,
          display: "flex",
          justifyContent: "center",
          py: 4,
        }}
      >
        <CircularProgress size={24} sx={{ color: "#724e41" }} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#ef4444", textAlign: "center" }}
        >
          {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
      <Typography
        sx={{ fontWeight: 700, fontSize: "15px", color: "#1e293b", mb: 2 }}
      >
        Package
      </Typography>
      <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 1 }}>
        {packages.map((pkg) => (
          <Box
            key={pkg.id}
            onClick={() => handleSelect(pkg)}
            sx={{
              flex: "0 0 calc(33.333% - 11px)",
              minWidth: 240,
              borderRadius: "12px",
              p: 2,
              border:
                selectedId === pkg.id
                  ? "2px solid #724e41"
                  : "2px solid #f0f4f8",
              bgcolor: selectedId === pkg.id ? "#fff8f2" : "white",
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { borderColor: "#c59a70" },
            }}
          >
            <Box sx={{ mb: 1 }}>{iconByIndex[pkg.id % iconByIndex.length]}</Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                color: "#1e293b",
                mb: 0.3,
              }}
            >
              {pkg.name}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "13px",
                color: "#475569",
                mb: 1.5,
              }}
            >
              ${pkg.price.toLocaleString()}.00
            </Typography>

            {pkg.features.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "#94a3b8",
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Features
                </Typography>
                <Stack spacing={0.5}>
                  {pkg.features.slice(0, 4).map((f, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={0.8}
                      alignItems="flex-start"
                    >
                      <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                        •
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#64748b",
                          lineHeight: 1.4,
                        }}
                      >
                        {f}
                      </Typography>
                    </Stack>
                  ))}
                  {pkg.features.length > 4 && (
                    <Typography
                      sx={{ fontSize: "10px", color: "#94a3b8", mt: 0.5 }}
                    >
                      +{pkg.features.length - 4} more
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {pkg.features.length === 0 && (
              <Typography
                sx={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}
              >
                No features listed
              </Typography>
            )}
          </Box>
        ))}

        {packages.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "#94a3b8", py: 2 }}>
            No packages available.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
